import { i18nFn, useSpringBigStore } from '@springbig/shared';
import React, { useState } from 'react';
import { useEffectOnce } from 'usehooks-ts';
import { Actionsheet, HStack, Input, Pressable, SmallCloseIcon, VStack } from 'native-base';
import { Caption } from '~/shared/Typography';
import { useGeneralStyles } from '~/shared/styles';

const PointsConverterField = ({
    t,
    onChange
}: {
    t: i18nFn,
    onChange: (value: number) => void;
}) => {

    const { member, merchant } = useSpringBigStore();
    const gStyle = useGeneralStyles();

    const maxValue = (
        merchant?.gift_card_points_restrictions ? Math.min(
            9999,
            member?.balance || 0,
            merchant?.gc_point_amount_one_time || 0,
            member?.remaining_point_amount_for_current_period || 0
        ) : Math.min(9999, member?.balance || 0)
    );

    const [convertPoints, setConvertPoints] = useState(maxValue);
    const [isMoreInfoOpen, setIsMoreInfoOpen] = useState(false);

    const handlePointsChange = (points: string): void => {

        const minValue = 0;

        let numericValue = points.replace(/[^0-9]/g, '').slice(0, 4);

        if (numericValue.length === 0 || numericValue === 'NaN') {
            numericValue = minValue.toString();
        }

        const finalAmount = Math.min(Math.max(parseInt(numericValue), minValue), maxValue);
        setConvertPoints(finalAmount);
    };

    const debouncePointsAmountNormalization = (): void => {

        // insures that the amount is divisible by the merchant's dollars_per_point value to avoid decimals
        let numericValue = convertPoints;

        if (numericValue % Number(merchant.dollars_per_point) !== 0) {
            numericValue = (Math.floor(numericValue / Number(merchant.dollars_per_point)) * Number(merchant.dollars_per_point));
        }

        setConvertPoints(numericValue);
        onChange(numericValue);
    };

    useEffectOnce(() => {

        // set initial point value to the highest value possible
        if (member && merchant) debouncePointsAmountNormalization();
    });

    return (
        <VStack>
            <Caption fontSize={18} fontWeight={300}>{ t('general.points') }</Caption>
            <HStack space={4} justifyContent={'space-between'} alignItems={'baseline'}>
                <HStack space={4} alignItems={'baseline'}>
                    <Input
                        borderColor={gStyle.button_background}
                        placeholder={t('giftCard.pointsConvertor.form.accountNumber.placeholder')}
                        onChangeText={handlePointsChange}
                        onBlur={debouncePointsAmountNormalization}
                        value={convertPoints.toString()}
                        keyboardType="numeric"
                        borderWidth={1}
                        fontSize={18}
                        fontWeight={200}
                        paddingX={8}
                        borderRadius={7}
                        textAlign='center'
                        maxWidth={'100px'}
                        returnKeyType='done'
                    />
                    <Caption fontSize={18} fontWeight={300}>points for</Caption>
                </HStack>
                <Caption fontSize={18} fontWeight={300} color={gStyle.button_background}>
                    ${ (convertPoints / parseFloat(merchant.dollars_per_point)).toFixed(2) }
                </Caption>
            </HStack>
            { merchant?.gift_card_points_restrictions &&
                member?.remaining_point_amount_for_current_period === merchant.gc_point_amount_per_period &&
                <Caption opacity={.6} fontSize={12}>
                    { t('giftCard.restrictions.point_limit', [
                        merchant.gc_point_amount_one_time,
                        merchant.gc_point_amount_per_period
                    ])
                    }
                </Caption>
            }
            {
                merchant?.gift_card_points_restrictions &&
                member &&
                member?.remaining_point_amount_for_current_period < merchant.gc_point_amount_one_time &&
                <Caption opacity={.6} fontSize={12}>
                    { t('giftCard.restrictions.one_time_point_limit_under', [
                        member?.remaining_point_amount_for_current_period,
                        merchant.gc_point_amount_per_period
                    ])
                    }
                </Caption>
            }
            {
                merchant?.gift_card_points_restrictions &&
                member &&
                member?.remaining_point_amount_for_current_period !== merchant.gc_point_amount_per_period &&
                member?.remaining_point_amount_for_current_period > merchant.gc_point_amount_one_time &&
                <Caption opacity={.6} fontSize={12}>
                    { t('giftCard.restrictions.one_time_point_limit_over', [
                        merchant.gc_point_amount_one_time,
                        member?.remaining_point_amount_for_current_period,
                        merchant.gc_point_amount_per_period
                    ])
                    }
                </Caption>
            }
            { merchant?.gift_card_points_restrictions && member.balance > 0 &&
                <Pressable onPress={() => setIsMoreInfoOpen(true)}>
                    <Caption fontSize={12} color={gStyle.link}>more info</Caption>
                </Pressable>
            }
            <Actionsheet isOpen={isMoreInfoOpen} onClose={() => setIsMoreInfoOpen(false)}>
                <Actionsheet.Content backgroundColor={gStyle.section_background} zIndex={9999} overflow='auto' w={'clamp(320px, 100vw, 480px)'} alignSelf='center'>
                    <Pressable onPress={() => setIsMoreInfoOpen(false)} position={'absolute'} top={4} right={4}>
                        <SmallCloseIcon size='md' color={gStyle.button_background} />
                    </Pressable>
                    <VStack space={4} p={4} w={'100%'}>
                        { merchant?.gift_card_restrictions &&
                            <VStack>
                                <Caption fontSize={18}>{ t('giftCard.restrictions.information.cash_title') }</Caption>
                                <Caption ml={4}>{ t('giftCard.restrictions.information.cash_one_time', [merchant?.gc_dollar_amount_one_time.split('.')[0] || 'no limit']) }</Caption>
                                <Caption ml={4}>{ t('giftCard.restrictions.information.cash_per_period', [merchant?.gc_dollar_amount_per_period.split('.')[0] || 'no limit']) }</Caption>
                                <Caption ml={4}>{ t('giftCard.restrictions.information.cash_period', [merchant?.gc_dollar_frequency_in_days || 'no limit']) }</Caption>
                            </VStack>
                        }
                        { merchant?.gift_card_points_restrictions &&
                            <VStack>
                                <Caption fontSize={18}>{ t('giftCard.restrictions.information.points_title') }</Caption>
                                <Caption ml={4}>{ t('giftCard.restrictions.information.points_one_time', [merchant?.gc_point_amount_one_time || 'no limit']) }</Caption>
                                <Caption ml={4}>{ t('giftCard.restrictions.information.points_per_period', [merchant?.gc_point_amount_per_period || 'no limit']) }</Caption>
                                <Caption ml={4}>{ t('giftCard.restrictions.information.points_period', [merchant?.gc_point_frequency_in_days || 'no limit']) }</Caption>
                            </VStack>
                        }
                    </VStack>
                </Actionsheet.Content>
            </Actionsheet>
        </VStack>
    );
};

export default PointsConverterField;
